import React, { useEffect, useState } from "react";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import EmbeddableScreen from "../../components/ui/EmbeddableScreen";
import SideBar from "../../components/SideBar";
import {
  formatCurrency,
  frenchDate,
  getDaysDurationBetweenDates,
  getDurationBetweenDates,
} from "../../variables/functions";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  ConfigProvider,
  DatePicker,
  Select,
  Spin,
  Table,
  Tabs,
  Badge,
  Card,
  Col,
  Row,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import frFR from "antd/locale/fr_FR";
import { rightsService } from "../../services/rightsService";
import axiosInstance from "../../variables/api/axiosInstance";
import jsPDF from "jspdf";
import { Filter } from 'lucide-react';
import { highlightMatch } from "../../variables/stringFunctions";
import { searchSinistre } from "../../hooks/useSearch";
import { drawHeaderSinistrePdfGenerator, generateSinistresExcel, sinistresPdfGenerator } from "./hooks/generatesHook";
import { styles } from "./hooks/listStyles";
import FiltersModal from "./components/list/FiltersModal";
import { allColumns, columns, stats, TAB_CONFIG, tabsDefinition } from "./components/utils/listData";
import { StatCard } from "../../components/statistics/StatCard";
import { SINISTER_COLORS } from "../../components/ui/colors";
import { LOGGED_USER } from "../../variables/constants";

const { RangePicker } = DatePicker;
const dateFormat = "DD/MM/YYYY";

const extractCreatorId = (sinistre) => {
  const candidate = sinistre?.createdBy ?? sinistre?.creator ?? sinistre?.createur;
  if (!candidate) return null;
  if (typeof candidate === "string") return candidate;
  if (candidate?.codeId) return candidate.codeId;
  if (candidate?.id) return candidate.id;
  return candidate;
};

const computeRedacteurDisplay = (row, namesMap) => {
  const creatorName = row?.createdBy && namesMap?.[row.createdBy];
  if (creatorName) return creatorName;
  if (row?.redacteurBase && row.redacteurBase !== "—") return row.redacteurBase;
  if (row?.createdBy) return row.createdBy;
  return "—";
};

// Tente d'extraire le nom du rédacteur du sinistre à partir des différentes formes possibles.
const resolveSinistreRedacteur = (sinistre) => {
  if (!sinistre) return "—";

  const candidates = [
    sinistre?.redacteur,
    sinistre?.redacteurSinistre,
    sinistre?.declarePar,
    sinistre?.declarantUtilisateur,
    sinistre?.utilisateur,
    sinistre?.user,
    sinistre?.createdBy,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }

    const person = candidate?.personne ?? candidate;
    const fullName = [person?.prenom, person?.nom].filter(Boolean).join(" ").trim();
    if (fullName) return fullName;

    const username = person?.username || person?.userName || person?.login;
    if (username) return username;
  }

  const declarant = [sinistre?.nomDeclarant, sinistre?.phoneDeclarant]
    .filter(Boolean)
    .join(" - ")
    .trim();
  return declarant || "—";
};

const ListSinistres = ({ embedded = false } = {}) => {
  const [data, setData] = useState([]);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loadingSinistres, setLoadingSinistres] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCompagnies, setSelectedCompagnies] = useState([]);
  const [membres, setMembres] = useState([]);
  const [branches, setBranches] = useState([]);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [excelPreviewUrl, setExcelPreviewUrl] = useState([]);
  const [count, setCount] = useState({});
  const [tabId, setTabId] = useState("1");
  const [searchResults, setSearchResults] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [creatorNames, setCreatorNames] = useState({});

  const navigate = useNavigate();
  const location = useLocation();

  const handleChange = (pagination, _filters, sorter) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    setPagination(pagination);
    setSortedInfo(sorter);
  };

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const hasActiveFilters = selectedYear !== new Date().getFullYear() || selectedMonths.length > 0;

  const handleDateChange = (_dates, dateStrings) => {
    const [sDate, eDate] = dateStrings;
    if (!sDate || !eDate) {
      setFilteredInfo((prev) => ({
        ...prev,
        startDate: undefined,
        endDate: undefined,
      }));
      fetchData({
        pagination,
        filters: { ...filteredInfo, startDate: undefined, endDate: undefined },
        tabKey: tabId,
      });
      return;
    }
    const [startDay, startMonth, startYear] = sDate.split("/");
    const [endDay, endMonth, endYear] = eDate.split("/");
    const startDate = `${startYear}-${startMonth}-${startDay}`;
    const endDate = `${endYear}-${endMonth}-${endDay}`;
    setFilteredInfo((prev) => ({ ...prev, startDate, endDate }));
    fetchData({ pagination, filters: { ...filteredInfo, startDate, endDate }, tabKey: tabId });
  };

  const filteringOnBranches = (branche) => {
    const nextFilters = { ...filteredInfo, branche: branche };
    setFilteredInfo(nextFilters);
    fetchData({ pagination, filters: nextFilters, tabKey: tabId });
  };

  const filteringOnCompanies = (companies) => {
    setSelectedCompagnies(companies);
    const nextFilters = { ...filteredInfo, compagnie: companies };
    setFilteredInfo(nextFilters);
    fetchData({ pagination, filters: nextFilters, tabKey: tabId });
  };

  const fetchData = async (params = {}) => {
    setLoadingSinistres(true);
    const activeTabKey = params.tabKey ?? tabId;
    const { url, statusFilter = [] } = TAB_CONFIG[activeTabKey] || TAB_CONFIG["1"];
    const providedFilters = params.filters ?? {};

    try {
      const response = await axiosInstance.get(url, {
        params: {
          page: (params.pagination?.current ?? pagination.current) - 1,
          size: params.pagination?.pageSize ?? pagination.pageSize,
          ...providedFilters,
          compagnie: providedFilters?.compagnie?.join(","),
          categorie: providedFilters?.categorie?.join(","),
          selectedYear,
          selectedMonths: selectedMonths ? selectedMonths.join(',') : null,
        },
      });

      const { content, totalElements } = response.data;
      // console.log("Une réponse", content);

      const rows = content.map((sinistre, idx) => {
        const etatCode = sinistre?.statutDossier;
        const normalizeEtat = (value) => {
          if (value === null || value === undefined) return "";
          if (typeof value === "number") return value.toString();
          if (typeof value === "string") {
            return value
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .trim()
              .toUpperCase();
          }
          return "";
        };
        const sanitizeEtat = (value) => normalizeEtat(value).replace(/[^A-Z0-9]/g, "");
        const sanitizedEtatCode = sanitizeEtat(etatCode);
        const pendingEtatCodes = ["A", "AVALIDER"];
        const refusedEtatCodes = ["RF", "REFUSE"];
        const aValider = pendingEtatCodes.includes(sanitizedEtatCode)
          || pendingEtatCodes.includes(sinistre?.sortDossier);
        const refused = refusedEtatCodes.includes(sinistre?.sortDossier)
          || refusedEtatCodes.includes(sinistre?.sortDossier);

        const numeroDisplay = (sinistre?.numeroSinistrem ?? sinistre?.numSinistre);
        const creatorId = extractCreatorId(sinistre);
        const redacteurBase = resolveSinistreRedacteur(sinistre);

        return {
          key: sinistre.codeId,
          id: idx,
          createdBy: creatorId,
          numeroPolice: sinistre.numeroPoliceM ? sinistre.numeroPoliceM : sinistre.numeroPoliceP,
          assure: sinistre?.prenom ? `${sinistre?.prenom} ${sinistre?.nom}` : `${sinistre?.nom}`,
          organisme: sinistre?.organisme,
          data: sinistre,
          dateSinistre: sinistre?.dateSinistre ? frenchDate(sinistre?.dateSinistre) : "-",
          dateDeclaration: sinistre?.dateDeclaration ? frenchDate(sinistre?.dateDeclaration) : frenchDate(sinistre?.createdAt),
          numeroSinistre: numeroDisplay,
          etat: sinistre?.etat,
          aValider,
          refused,
          sap: sinistre?.sap ? `${formatCurrency(sinistre?.sap)}` : 0,
          evaluation: sinistre?.evaluation ? `${formatCurrency(sinistre?.evaluation)}` : 0,
          duree: getDurationBetweenDates(
            sinistre?.dateSinistre,
            sinistre?.dateDeclaration ?? sinistre?.createdAt
          ),
          redacteurBase,
          redacteur: computeRedacteurDisplay(
            { createdBy: creatorId, redacteurBase },
            creatorNames
          ),
          daysDuree: getDaysDurationBetweenDates(
            sinistre?.dateSinistre,
            sinistre?.dateDeclaration ?? sinistre?.createdAt
          ),
        };
      });
      // console.log("Rows", rows);
      setData(rows);
      const effectiveTotal = statusFilter.length > 0 ? rows.length : totalElements;
      setPagination((prev) => ({
        ...(params.pagination ?? prev),
        total: effectiveTotal,
      }));
      await fetchCreatorNames(rows);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingSinistres(false);
    }
  };

  useEffect(() => {
    if (location.state?.goToTab) {
      setTabId(location.state.goToTab);
    }
  }, [location.state]);

  useEffect(() => {
    axiosInstance
      .get(`/api/sinistre/status-count`, {
        params: { selectedYear, selectedMonths: selectedMonths ? selectedMonths?.join(',') : null },
      })
      .then((res) => setCount(res.data))
      .catch((err) => console.log(err));

    axiosInstance
      .get(`/api/sinistre/non-valide/count`, {
        params: { selectedYear, selectedMonths: selectedMonths ? selectedMonths?.join(',') : null },
      })
      .then((res) =>
        setCount((prev) => ({
          ...prev,
          totalSinistresNonValides: res.data?.totalSinistresNonValides ?? 0,
        }))
      )
      .catch((err) => console.log(err));

    if (selectedDate || selectedCompagnies.length) {
      if (selectedDate) {
        const [startDate, endDate] = selectedDate;
        fetchData({
          pagination,
          filters: {
            ...filteredInfo,
            startDate,
            endDate,
            compagnie: selectedCompagnies,
          },
          tabKey: tabId,
        });
      } else {
        fetchData({
          pagination,
          filters: { ...filteredInfo, compagnie: selectedCompagnies },
          tabKey: tabId,
        });
      }
    } else {
      fetchData({ pagination, filters: filteredInfo, tabKey: tabId });
    }

  }, [currentPage, pageSize, selectedDate, tabId, selectedMonths, selectedYear]);

  useEffect(() => {
    axiosInstance
      .get(`/api/organisme/assurance`)
      .then((res) => {
        const membs = res.data.map((memb, index) => ({
          key: index,
          text: memb.raisonSociale,
          label: memb.raisonSociale,
          value: memb.code,
          data: memb,
        }));
        membs.push({
          key: "POOL",
          text: "POOL",
          label: "POOL TPVM-VT GIE",
          value: "00",
        });
        setMembres(membs);
      })
      .catch((err) => console.log(err));

    axiosInstance
      .get(`/api/branche`)
      .then((res) => {
        const brs = res.data.map((br, index) => ({
          key: index,
          label: br.nom,
          value: br.codeBranche,
          data: br,
        }));
        setBranches(brs);
      })
      .catch((err) => console.log(err));
  }, []);

  const getTabColumns = (key) => (["1", "2", "3", "4", "5"].includes(key) ? columns(navigate) : allColumns(navigate));
  const getTabDataSource = (key) => (key === "6" ? data.filter((r) => r.etat === 0) : data);

  const generateSinistresPdf = (sinistres) => {
    setLoadingSinistres(true);
    const doc = new jsPDF({
      orientation: "landscape", // ✅ PAYSAGE
      unit: "mm",
      format: "a4"
    });

    const grouped = {};

    const now = new Date();
    const formattedDateTime = now.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    // 3e arg = organisme/compagnie (non applicable ici) ; ne pas passer la date (serait affichée comme organisme).
    drawHeaderSinistrePdfGenerator(doc, pageWidth);

    axiosInstance.get(`/api/sinistre/etat-cima-v2`)
      .then((response) => {
        const sinistres = response.data;
        setExcelPreviewUrl(response.data);
        const dataUri = sinistresPdfGenerator(sinistres, doc, grouped, formattedDateTime);
        setPdfPreviewUrl(dataUri);
        setLoadingSinistres(false);
      }).catch((error) => {
        console.error("Error fetching sinistres for PDF generation:", error);
        setLoadingSinistres(false);
      });

  }


  const applyFilters = () => {
    setShowFilters(false);
    // Ici vous pouvez ajouter la logique pour appliquer les filtres
    console.log('Filtres appliqués:', { selectedYear, selectedMonths });
  };

  const closePreview = () => setPdfPreviewUrl(null);
  const onTabsChange = (key) => {
    setTabId(key);
    setPagination((prev) => ({ ...prev, current: 1 }));
    setCurrentPage(1);
  };

  const renderSearchResults = (results) => {
    return <div className="px-3 py-2">
      <h6 className="text-muted text-uppercase mb-2" />
      {results.map((item, index) => (
        <div
          key={index}
          onClick={() =>
            navigate("/details-sinistre/v2", {
              state: { codeId: item.codeId },
            })
          }
          className="dropdown-item d-flex align-items-center gap-2 py-1"
          style={{ cursor: "pointer" }}
        >
          <span className="text-truncate">
            {[
              (item?.numeroSinistrem || item?.numSinistre),
              item?.numeroPoliceM
                ? item?.numeroPoliceM
                : item?.numeroPoliceP,
              frenchDate(item?.dateSinistre),
              item?.immatriculation,
              item?.nom,
              item?.prenom || "",
            ].map((text, i) => (
              <React.Fragment key={i}>
                {i > 0 && " - "}
                {highlightMatch(text?.toString(), searchValue)}
              </React.Fragment>
            ))}
          </span>
        </div>
      ))}
    </div>;
  }

  useEffect(() => {
    if (searchValue && searchValue?.length > 2) {
      searchSinistre(searchValue).then((res) => {
        setSearchResults(res.data.content);
      }).catch((err) => {
        console.log(err);
      });
    }
  }, [searchValue]);

  const fetchCreatorNames = async (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const missingIds = [...new Set(rows.map((r) => r.createdBy).filter(Boolean))]
      .filter((id) => !creatorNames[id]);
    if (!missingIds.length) return;

    try {
      const responses = await Promise.all(
        missingIds.map((id) =>
          axiosInstance
            .get(`/api/utilisateur/codeId/${id}`)
            .then((res) => {
              const user = res?.data;
              const fullname = [user?.prenom, user?.nom].filter(Boolean).join(" ").trim();
              return { id, name: fullname || user?.login || user?.username || id };
            })
            .catch(() => ({ id, name: id }))
        )
      );
      const namesMap = responses.reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {});
      setCreatorNames((prev) => ({ ...prev, ...namesMap }));
    } catch (err) {
      console.error("Erreur chargement rédacteurs sinistres:", err);
    }
  };

  useEffect(() => {
    if (!Object.keys(creatorNames).length) return;
    setData((prev) =>
      prev.map((row) => ({
        ...row,
        redacteur: computeRedacteurDisplay(row, creatorNames),
      }))
    );
  }, [creatorNames]);

  const exportFloating = (
    <Spin spinning={loadingSinistres}>
      <div className="buy-now">
        <button
          onClick={() => generateSinistresPdf()}
          className="btn btn-secondary btn-buy-now"
        >
          Exporter
        </button>
      </div>
    </Spin>
  );

  return (
    <EmbeddableScreen
      embedded={embedded}
      currentMenu={"SINISTRES"}
      navBarProps={{
        filteredResults: searchResults,
        setFilteredResults: setSearchResults,
        renderSearchResults,
        searchValue,
        setSearchValue,
      }}
      extra={exportFloating}
    >
      <div className="container-xxl flex-grow-1 container-p-y">
              <div style={{ width: '100%', maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
                {/* En-tête avec bouton filtres */}
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                  <button
                    onClick={() => setShowFilters(true)}
                    style={{ ...styles.openFiltersButton, backgroundColor: hasActiveFilters ? '#8b5cf6' : '#212466', }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = hasActiveFilters ? '#7c3aed' : '#1d4ed8'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = hasActiveFilters ? '#8b5cf6' : '#2563eb'}
                  >
                    <Filter style={{ width: '16px', height: '16px' }} />
                    Filtres
                    {hasActiveFilters && (
                      <span style={styles.selectedItemsSpan}>
                        {selectedMonths.length || '!'}
                      </span>
                    )}
                  </button>
                </div>

                {/* Cartes de statistiques */}
                <Row style={{ marginBottom: "22px", justifyContent: "space-between" }}>
                  {stats(count).map((stat, index) => {
                    return (
                      <Col
                        key={index}
                        flex="1" // ✅ prend la même largeur que les autres
                        style={{
                          minWidth: "180px", // optionnel, empêche les cards trop petites
                          marginRight: "10px",
                        }}
                      >
                        <StatCard
                          title={stat.title}
                          subtitle={stat.subtitle}
                          value={stat.value}
                          icon={stat.icon}
                          iconBg={SINISTER_COLORS.primary}
                          gradient={SINISTER_COLORS.primary}
                        />
                      </Col>
                    );
                  })}
                </Row>

                {/* Modal des filtres */}
                {showFilters && (
                  <FiltersModal setShowFilters={setShowFilters} styles={styles} hasActiveFilters={hasActiveFilters} applyFilters={applyFilters} selectedYear={selectedYear} setSelectedYear={setSelectedYear} selectedMonths={selectedMonths} setSelectedMonths={setSelectedMonths} />
                )}
              </div>

              <div className="row">
                <div className="col-sm-4">
                  {(rightsService("HCO022") ||
                    rightsService("HCO005") ||
                    rightsService("HCO019")) && (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        style={{
                          background: `${SINISTER_COLORS.primary}`,
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: 600,
                          boxShadow: `0 4px 12px ${SINISTER_COLORS.primary}40`,
                          marginBottom: '10px',
                          marginTop: '-20px',
                        }}
                        onClick={() => navigate("/declarer-sinister")}
                      >
                        Déclarer un sinistre
                      </Button>
                    )}
                </div>
              </div>

              {/* Nouvelle interface professionnelle */}
              <Card className="filters-card-pro" style={{ marginTop: "0px" }} bordered={false}>
                <div className="row">
                  <div className="col-4">
                    <label className="filter-label-pro">Période de déclaration</label>
                    <RangePicker
                      format={dateFormat}
                      onChange={handleDateChange}
                      placeholder={['Date début', 'Date fin']}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="col-4">
                    <label className="filter-label-pro">Branche</label>
                    <Select
                      placeholder="Sélectionner une branche"
                      onChange={filteringOnBranches}
                      options={branches}
                      style={{ width: "100%" }}
                      maxTagCount="responsive"
                    />
                  </div>
                  {LOGGED_USER?.profil?.typeOrganisme === "POOL" ? <div className="col-4">
                    <label className="filter-label-pro">Compagnies</label>
                    <Select
                      mode="multiple"
                      placeholder="Sélectionner des compagnies"
                      onChange={filteringOnCompanies}
                      options={membres}
                      style={{ width: "100%" }}
                      maxTagCount="responsive"
                    />
                  </div> : null}
                </div>
              </Card>

              <Card className="main-card-pro" bordered={false}>
                <ConfigProvider locale={frFR}>
                  <Spin spinning={loadingSinistres} size="large">
                    <Tabs
                      onChange={onTabsChange}
                      activeKey={tabId}
                      className="custom-tabs-pro"
                      size="large"
                      items={tabsDefinition(data, count).map(({ key, label, icon, count: tabCount }) => ({
                        key,
                        label: (
                          <span>
                            {icon}
                            {label}
                            <Badge
                              count={tabCount}
                              className="tab-badge-pro"
                              style={{
                                backgroundColor: key === tabId ? 'white' : '#667eea',
                                color: key === tabId ? '#667eea' : 'white'
                              }}
                            />
                          </span>
                        ),
                        children: (
                          <div className="tab-content-wrapper-pro">
                            <Table
                              columns={getTabColumns(key)}
                              dataSource={getTabDataSource(key)}
                              onChange={handleChange}
                              rowKey="key"
                              pagination={pagination}
                              size="middle"
                            />
                          </div>
                        ),
                      }))}
                    />
                  </Spin>
                </ConfigProvider>
              </Card>

              {pdfPreviewUrl && (
                <div
                  style={styles.pdfPreviewContainer}
                >
                  <div
                    style={styles.pdfPreviewSubContainer}
                  >
                    <button
                      onClick={() => setPdfPreviewUrl(null)}
                      style={styles.pdfPreviewCloseButton}
                    >
                      Fermer
                    </button>
                    <div className="text-center">PREVISUALISATION</div>
                    <iframe
                      src={pdfPreviewUrl}
                      style={{ flexGrow: 1, border: "none" }}
                      title="Aperçu PDF"
                    ></iframe>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "15px",
                        marginTop: "20px",
                      }}
                    >
                      <a
                        href={pdfPreviewUrl}
                        download={`DONNEES DU TABLEAU F DES ETATS CIMA  ${new Date().toLocaleString(
                          "fr-FR",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}.pdf`}
                        style={styles.downloadPdfButton}
                      >
                        Télécharger le PDF
                      </a>
                      <button
                        onClick={() => generateSinistresExcel(excelPreviewUrl)}
                        style={styles.downloadExcelButton}
                      >
                        Télécharger l'Excel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
    </EmbeddableScreen>
  );
};

export default ListSinistres;
